import { ChartOptions, TooltipItem } from 'chart.js';
import { JSX, useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { DailyStatistics } from '../../dto/statistics';
import { useLayout } from '../../hooks/layout.hook';
import { useStatistics } from '../../hooks/statistics.hook';
import { getCutoffDate, getLocaleDate, getLocaleShortDate, getThemeColor, TimeFrame } from '../../util/util';

interface StatMetric {
  key: keyof DailyStatistics;
  label: string;
  shortLabel: string;
  color: string;
}

const METRICS: StatMetric[] = [
  { key: 'totalUsers', label: 'Total users', shortLabel: 'Total', color: '--color-secondary' },
  { key: 'activeUsers', label: 'Active users', shortLabel: 'Active', color: '--color-primary' },
  { key: 'activeHabits', label: 'Active habits', shortLabel: 'Habits', color: '--color-secondary' },
  { key: 'recordsCreated', label: 'Habits tracked', shortLabel: 'Tracked', color: '--color-primary' },
  { key: 'activePartnerships', label: 'Active partnerships', shortLabel: 'Partnerships', color: '--color-secondary' },
  { key: 'readingsCompleted', label: 'Readings completed', shortLabel: 'Readings', color: '--color-primary' },
];

export default function AppStatisticScreen(): JSX.Element {
  const { t } = useTranslation();
  const { loadStatistics, resetStatistics, statisticsLoading, statistics } = useStatistics();
  const [timeFrame, setTimeFrame] = useState<TimeFrame>(TimeFrame.MONTH);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const unsubscribe = loadStatistics();
    return () => {
      unsubscribe();
      resetStatistics();
    };
  }, []);

  const filteredStats = useMemo(() => {
    if (!statistics.length) return [];

    const cutoff = getCutoffDate(timeFrame);
    return statistics.filter((s) => s.date >= cutoff);
  }, [statistics, timeFrame]);

  const hasOldData = statistics.length > 0 && statistics[0].date < getCutoffDate(TimeFrame.WEEK);

  useLayout(
    () => ({
      activeMenu: 'settings',
      title: t('App Statistics'),
      loading: statisticsLoading,
      backButton: true,
    }),
    [t, statisticsLoading],
  );

  return statistics.length === 0 ? (
    <div className="text-center text-base-content/70">{t('No statistics available yet')}</div>
  ) : (
    <div className="flex flex-col gap-10">
      {filteredStats.length === 0 ? (
        <div className="text-center text-base-content/70 italic">{t('No data in this timeframe')}</div>
      ) : (
        <>
          <LatestStats today={filteredStats.at(-1)} yesterday={filteredStats.at(-2)} />

          <HistoryStats
            data={filteredStats}
            hasOldData={hasOldData}
            timeFrame={timeFrame}
            setTimeFrame={setTimeFrame}
          />
        </>
      )}
    </div>
  );
}

interface LatestStatsProps {
  today?: DailyStatistics;
  yesterday?: DailyStatistics;
}

function LatestStats({ today, yesterday }: LatestStatsProps): JSX.Element | undefined {
  const { t } = useTranslation();
  const [showYesterday, setShowYesterday] = useState(!!yesterday);
  const stats = showYesterday && yesterday ? yesterday : today;

  return (
    stats && (
      <div className="flex flex-col gap-5">
        <h2 className="text-2xl text-center">{t('Latest')}</h2>

        {yesterday && (
          <div className="flex justify-center">
            <div className="join bg-base-100 rounded-lg">
              <button
                className={`join-item btn btn-sm ${showYesterday ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setShowYesterday(true)}
              >
                {t('Yesterday')}
              </button>
              <button
                className={`join-item btn btn-sm ${!showYesterday ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setShowYesterday(false)}
              >
                {t('Today')}
              </button>
            </div>
          </div>
        )}

        <div className="bg-base-100 rounded-lg p-4">
          <div className="text-center text-base-content/70 text-sm mb-3">{getLocaleDate(stats.date)}</div>
          <div className="grid grid-cols-2 gap-2">
            {METRICS.map((metric) => (
              <div key={metric.key} className="flex flex-col items-center">
                <span className="text-2xl font-bold">{stats[metric.key] as number}</span>
                <span className="text-base-content/70 text-sm text-center">{t(metric.label)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  );
}

interface HistoryStatsProps {
  data: DailyStatistics[];
  hasOldData: boolean;
  timeFrame: TimeFrame;
  setTimeFrame: (timeFrame: TimeFrame) => void;
}

function HistoryStats({ data, hasOldData, timeFrame, setTimeFrame }: HistoryStatsProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-2xl text-center">{t('History')}</h2>

      {hasOldData && (
        <select
          className="select select-lg w-full"
          value={timeFrame}
          onChange={(e) => setTimeFrame(e.target.value as TimeFrame)}
        >
          {Object.values(TimeFrame).map((tf) => (
            <option key={tf} value={tf}>
              {t(tf)}
            </option>
          ))}
        </select>
      )}

      <StatsChart data={data} metrics={METRICS.slice(0, 2)} title={t('Users')} />
      <StatsChart data={data} metrics={METRICS.slice(2, 4)} title={t('Habits')} />
      <StatsChart data={data} metrics={METRICS.slice(4, 6)} title={t('Other')} />
    </div>
  );
}

interface StatsChartProps {
  data: DailyStatistics[];
  metrics: StatMetric[];
  title: string;
}

function StatsChart({ data, metrics, title }: StatsChartProps): JSX.Element {
  const { t } = useTranslation();

  const { chartData, options } = useMemo(() => {
    const chartData = {
      labels: data.map((d) => d.date),
      datasets: metrics.map((metric) => ({
        label: t(metric.shortLabel),
        data: data.map((d) => d[metric.key] as number),
        fill: false,
        borderColor: getThemeColor(metric.color),
        backgroundColor: getThemeColor(metric.color),
        tension: 0.4,
        pointRadius: data.length > 30 ? 0 : 3,
      })),
    };

    const options: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: true,
      animation: false,
      plugins: {
        legend: {
          display: true,
          labels: {
            boxWidth: 12,
            padding: 8,
          },
        },
        tooltip: {
          callbacks: {
            title: (context: TooltipItem<'line'>[]) => getLocaleDate(chartData.labels[context[0].dataIndex]),
            label: (context: TooltipItem<'line'>) => `${context.dataset.label}: ${context.raw}`,
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 6,
            callback: (index: string | number) => {
              return getLocaleShortDate(chartData.labels[+index]);
            },
          },
        },
        y: {
          min: 0,
        },
      },
    };

    return { chartData, options };
  }, [t, data, metrics]);

  return (
    <div className="bg-base-100 rounded-lg p-3">
      <h3 className="text-center font-semibold mb-2">{title}</h3>
      <Line data={chartData} options={options} />
    </div>
  );
}
